import { useState, useEffect } from 'react'
import { Moon, Sun, Lock, User, Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { setAuthCookie, verifyWithWorker } from '../../utils/auth'

function Login() {
  const navigate = useNavigate()
  const [isDark, setIsDark] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [isDark])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    // 使用 Cloudflare Worker 验证
    const result = await verifyWithWorker(username, password)

    if (!result.success) {
      setError(result.error || '用户名或密码错误')
      setIsLoading(false)
      return
    }

    // 登录成功 - 使用 cookie
    const userData = result.user
    setAuthCookie({
      username: userData.username,
      displayName: userData.name,
      role: userData.role
    })

    // 跳转到主页面（使用 window.location 确保状态更新）
    window.location.href = '/dashboard'
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-slate-950 text-slate-100' : 'bg-slate-50 text-slate-900'}`}>
      <header className="p-4 flex justify-end items-center absolute w-full top-0 z-10">
        <button 
          onClick={() => setIsDark(!isDark)} 
          className={`p-2 rounded-full transition-colors ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-yellow-400' : 'bg-white hover:bg-slate-200 text-slate-600 shadow-sm'}`}
        >
          {isDark ? <Sun size={20} /> : <Moon size={20} />}
        </button>
      </header>

      <main className="container mx-auto px-4 min-h-screen flex flex-col items-center justify-center py-12">
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className={`h-16 md:h-20 w-16 md:w-20 rounded-xl flex items-center justify-center text-2xl font-bold ${isDark ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white'}`}>
              BQ
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">
            招投标质疑数据标注平台
          </h1>
          <p className="text-xl text-slate-500 dark:text-slate-400 font-light">Bidding Q&A Annotation Platform</p>
        </div>

        <div className="w-full max-w-md">
          <div className={`p-8 rounded-2xl shadow-xl border ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-100'}`}>
            <div className="text-center mb-8">
              <h2 className="text-2xl font-semibold">账号登录</h2>
              <p className="text-sm text-slate-500 mt-2">请输入您的企业账号</p>
            </div>
            
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                {error}
              </div>
            )}
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium ml-1">用户名</label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                  <input 
                    type="text" 
                    required 
                    placeholder="请输入用户名"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className={`w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 outline-none transition-all ${isDark ? 'bg-slate-800 border-slate-700 focus:border-blue-500 text-white' : 'bg-slate-50 border-slate-200'}`}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium ml-1">密码</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                  <input 
                    type="password" 
                    required
                    placeholder="请输入密码"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 outline-none transition-all ${isDark ? 'bg-slate-800 border-slate-700 focus:border-blue-500 text-white' : 'bg-slate-50 border-slate-200'}`}
                  />
                </div>
              </div>

              <button 
                type="submit" 
                disabled={isLoading}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white rounded-lg font-medium shadow-lg shadow-blue-500/30 transition-all hover:scale-[1.02] active:scale-[0.98] mt-4 flex items-center justify-center gap-2"
              >
                {isLoading ? <><Loader2 className="animate-spin" size={18} /> 登录中...</> : '登 录'}
              </button>
            </form>

            <div className="mt-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-xs text-blue-600 dark:text-blue-400">
              <p className="font-medium">请输入企业分配的用户名和密码</p>
            </div>
          </div>
        </div>
      </main>
      
      <footer className="py-6 text-center text-slate-400 text-sm">© 2026 Bidding Annotation Platform. All rights reserved.</footer>
    </div>
  )
}

export default Login
